import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { geodeticToENU } from '../utils/geodetic';
import {
  clampWorldXZToCircle,
  type ScreenPos,
  type WorldPos,
  projectToScreen,
  worldDistanceXZ,
} from '../utils/measureUtils';

interface FlythroughToolProps {
  viewerRef: React.MutableRefObject<ViewerLike | null>;
  isActive: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
  initialGeoTarget?: { latitude: number; longitude: number };
  initialWorldTarget?: [number, number, number];
  autoStartFromCamera?: boolean;
  initialOrbit?: {
    center: [number, number, number];
    radius: number;
    durationMs: number;
    autoStart?: boolean;
    loop?: boolean;
  };
  headless?: boolean;
  interactiveRetarget?: boolean;
  paused?: boolean;
  restartOnResume?: boolean;
  onManualNavigation?: () => void;
  retargetArea?: {
    center: [number, number, number];
    radius: number;
  };
}

type FlythroughStep = 'idle' | 'capturing' | 'preview' | 'orbiting' | 'complete';
type RecordingState = 'idle' | 'recording' | 'processing' | 'ready' | 'failed';

type OrbitCenter = {
  worldPos: WorldPos;
};

interface FrozenViewerState {
  movementSpeed: number;
  lookSpeed: number;
  maxClickToMoveDistance: number;
  fpsMovementEnabled?: boolean;
  pointerControlsEnabled?: boolean;
}

interface RecordedFlythrough {
  url: string;
  filename: string;
}

interface ViewerLike {
  camera?: THREE.Camera & { position: THREE.Vector3 };
  controls?: {
    movementSpeed?: number;
    lookSpeed?: number;
  };
  observerCoordinates?: number[] | null;
  maxClickToMoveDistance?: number;
  clickTransitioning?: boolean;
  transitioningCameraTarget?: boolean;
  nextCamera?: { copy: (camera: THREE.Camera) => void };
  previousCamera?: { copy: (camera: THREE.Camera) => void };
  raycaster?: {
    setFromCameraAndScreenPosition: (
      camera: THREE.Camera,
      screenPosition: THREE.Vector2,
      screenDimensions: THREE.Vector2
    ) => void;
    intersectSplatMesh: (splatMesh: unknown, outHits: unknown[]) => unknown;
  };
  splatMesh?: unknown;
  getRenderDimensions?: (outDimensions: THREE.Vector2) => void;
  setScriptedCameraActive?: (active: boolean) => void;
  resetCamera?: () => void;
  renderer?: {
    domElement?: HTMLCanvasElement;
  };
}

const MIN_ORBIT_RADIUS = 2.5;
const BASE_MAX_ORBIT_RADIUS = 18;
const DEFAULT_ORBIT_DURATION_MS = 8000;
const MIN_ORBIT_DURATION_MS = DEFAULT_ORBIT_DURATION_MS;
const MAX_ORBIT_DURATION_MS = 60000;
const ORBIT_PREVIEW_SAMPLES = 64;
const RECORDING_FRAME_RATE = 30;
const RECORDING_VIDEO_BITRATE = 8_000_000;
const RECORDING_START_DELAY_MS = 500;

function clampOrbitRadius(radius: number): number {
  return Math.max(MIN_ORBIT_RADIUS, radius);
}

function clampOrbitDuration(durationMs: number): number {
  return Math.max(MIN_ORBIT_DURATION_MS, Math.min(MAX_ORBIT_DURATION_MS, durationMs));
}

function getSpeedLabel(durationMs: number): string {
  if (durationMs <= DEFAULT_ORBIT_DURATION_MS) return 'Normal';
  if (durationMs >= 16000) return 'Slower';
  return 'Slow';
}

function computeDefaultOrbitRadius(
  center: WorldPos,
  camera: THREE.Camera & { position: THREE.Vector3 }
): number {
  const xzDist = worldDistanceXZ(center, {
    x: camera.position.x,
    y: camera.position.y,
    z: camera.position.z,
  });
  // For aerial cameras that are nearly directly above the center, the XZ distance
  // is near zero — use the height offset as the basis so the orbit is visible.
  const heightOffset = Math.abs(camera.position.y - center.y);
  return clampOrbitRadius(Math.max(xzDist, heightOffset * 0.6));
}

function computeRadiusSliderMax(defaultRadius: number): number {
  return Math.max(BASE_MAX_ORBIT_RADIUS, Math.ceil(defaultRadius * 1.5));
}

function buildOrbitPreviewPoints(
  center: WorldPos,
  radius: number,
  sampleCount: number
): WorldPos[] {
  const points: WorldPos[] = [];

  for (let index = 0; index <= sampleCount; index += 1) {
    const angle = (index / sampleCount) * Math.PI * 2;
    points.push({
      x: center.x + Math.cos(angle) * radius,
      y: center.y,
      z: center.z + Math.sin(angle) * radius,
    });
  }

  return points;
}

function buildVisiblePolylineSegments(points: ScreenPos[]): string[] {
  const segments: string[] = [];
  let currentSegment: string[] = [];

  for (const point of points) {
    if (point.visible) {
      currentSegment.push(`${point.x},${point.y}`);
      continue;
    }

    if (currentSegment.length >= 3) {
      segments.push(currentSegment.join(' '));
    }
    currentSegment = [];
  }

  if (currentSegment.length >= 3) {
    segments.push(currentSegment.join(' '));
  }

  return segments;
}

function freezeViewerControls(viewer: ViewerLike | null): FrozenViewerState {
  const movementSpeed = viewer?.controls?.movementSpeed ?? 5;
  const lookSpeed = viewer?.controls?.lookSpeed ?? 10;
  const maxClickToMoveDistance = viewer?.maxClickToMoveDistance ?? 15;
  const sparkControls = (viewer as any)?.sparkControls as any;
  const fpsMovementEnabled = sparkControls?.fpsMovement?.enable;
  const pointerControlsEnabled = sparkControls?.pointerControls?.enable;

  if (viewer?.controls) {
    viewer.controls.movementSpeed = 0;
    viewer.controls.lookSpeed = 0;
  }

  if (viewer) {
    viewer.setScriptedCameraActive?.(true);
    viewer.maxClickToMoveDistance = 0;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera && viewer.camera) viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera && viewer.camera) viewer.previousCamera.copy(viewer.camera);
    // Disable SparkControls entirely so the aerial spring can't override camera position.
    const sc = sparkControls;
    if (sc) {
      sc.flyTo?.cancel?.();
      if (sc.fpsMovement?.enable !== undefined) sc.fpsMovement.enable = false;
      const pc = sc.pointerControls as any;
      if (pc) {
        if (pc.enable !== undefined) pc.enable = false;
        pc.rotateVelocity?.set?.(0, 0, 0);
        pc.moveVelocity?.set?.(0, 0, 0);
      }
    }
  }

  return {
    movementSpeed,
    lookSpeed,
    maxClickToMoveDistance,
    fpsMovementEnabled,
    pointerControlsEnabled,
  };
}

function restoreViewerControls(viewer: ViewerLike | null, frozenState: FrozenViewerState | null) {
  if (!viewer || !frozenState) return;

  if (viewer.controls) {
    viewer.controls.movementSpeed = frozenState.movementSpeed;
    viewer.controls.lookSpeed = frozenState.lookSpeed;
  }

  viewer.maxClickToMoveDistance = frozenState.maxClickToMoveDistance;

  // Re-enable SparkControls that were disabled during orbit.
  const sc = (viewer as any).sparkControls as any;
  if (sc) {
    if (sc.fpsMovement?.enable !== undefined) {
      sc.fpsMovement.enable = frozenState.fpsMovementEnabled ?? true;
    }
    const pc = sc.pointerControls as any;
    if (pc?.enable !== undefined) {
      pc.enable = frozenState.pointerControlsEnabled ?? true;
    }
  }
  viewer.setScriptedCameraActive?.(false);
}

function pickWorldPointFromPointer(
  viewer: ViewerLike | null,
  clientX: number,
  clientY: number
): WorldPos | null {
  const canvas = viewer?.renderer?.domElement;
  const camera = viewer?.camera;
  const raycaster = viewer?.raycaster;
  const splatMesh = viewer?.splatMesh;
  const getRenderDimensions = viewer?.getRenderDimensions;

  if (!canvas || !camera || !raycaster || !splatMesh || !getRenderDimensions) {
    console.warn('[FlythroughTool] Missing picker dependencies', {
      hasCanvas: !!canvas,
      hasCamera: !!camera,
      hasRaycaster: !!raycaster,
      hasSplatMesh: !!splatMesh,
      hasGetRenderDimensions: !!getRenderDimensions,
    });
    return null;
  }

  const rect = canvas.getBoundingClientRect();
  const renderDimensions = new THREE.Vector2();
  const outHits: unknown[] = [];

  getRenderDimensions.call(viewer, renderDimensions);
  camera.updateMatrixWorld();

  const localX = clientX - rect.left;
  const localY = clientY - rect.top;

  const screenPosition = new THREE.Vector2(
    (localX / rect.width) * renderDimensions.x,
    (localY / rect.height) * renderDimensions.y
  );

  console.log('[FlythroughTool] pick pointer', {
    clientX,
    clientY,
    localX: Number(localX.toFixed(3)),
    localY: Number(localY.toFixed(3)),
    rectWidth: Number(rect.width.toFixed(3)),
    rectHeight: Number(rect.height.toFixed(3)),
    renderWidth: Number(renderDimensions.x.toFixed(3)),
    renderHeight: Number(renderDimensions.y.toFixed(3)),
    screenX: Number(screenPosition.x.toFixed(3)),
    screenY: Number(screenPosition.y.toFixed(3)),
  });

  raycaster.setFromCameraAndScreenPosition(camera, screenPosition, renderDimensions);
  const returnValue = raycaster.intersectSplatMesh(splatMesh, outHits);

  const hits = Array.isArray(returnValue)
    ? returnValue
    : Array.isArray(outHits)
      ? outHits
      : [];

  console.log('[FlythroughTool] raycast returnValue', returnValue);
  console.log('[FlythroughTool] raycast outHits', outHits);

  if (!hits.length) {
    console.warn('[FlythroughTool] Raycast returned no hits');
    return null;
  }

  const firstHit = hits[0] as any;
  console.log('[FlythroughTool] firstHit', firstHit);
  console.log(
    '[FlythroughTool] firstHit keys',
    typeof firstHit === 'object' && firstHit !== null ? Object.keys(firstHit) : []
  );

  const origin = firstHit?.origin;
  console.log('[FlythroughTool] origin raw', origin);

  const x = Number(origin?.x);
  const y = Number(origin?.y);
  const z = Number(origin?.z);

  console.log('[FlythroughTool] origin numeric', { x, y, z });
  if (camera?.position) {
    console.log('[FlythroughTool] camera position', {
      x: Number(camera.position.x.toFixed(2)),
      y: Number(camera.position.y.toFixed(2)),
      z: Number(camera.position.z.toFixed(2)),
    });
  }

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    console.warn('[FlythroughTool] Hit origin coordinates are not finite', { x, y, z, origin });
    return null;
  }

  return { x, y, z };
}

// Raycast from viewport center — gives the exact 3D surface point the camera is looking at.
function pickWorldPointFromCenter(viewer: ViewerLike | null): WorldPos | null {
  const camera = viewer?.camera;
  const raycaster = viewer?.raycaster;
  const splatMesh = viewer?.splatMesh;
  const getRenderDimensions = viewer?.getRenderDimensions;
  if (!camera || !raycaster || !splatMesh || !getRenderDimensions) return null;

  const renderDimensions = new THREE.Vector2();
  getRenderDimensions.call(viewer, renderDimensions);
  camera.updateMatrixWorld();

  const screenCenter = new THREE.Vector2(renderDimensions.x / 2, renderDimensions.y / 2);
  raycaster.setFromCameraAndScreenPosition(camera, screenCenter, renderDimensions);

  const outHits: unknown[] = [];
  const returnValue = raycaster.intersectSplatMesh(splatMesh, outHits);
  const hits = Array.isArray(returnValue) ? returnValue : Array.isArray(outHits) ? outHits : [];
  if (!hits.length) return null;

  const origin = (hits[0] as any)?.origin;
  const x = Number(origin?.x);
  const y = Number(origin?.y);
  const z = Number(origin?.z);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;

  return { x, y, z };
}

// Fallback: project camera forward by `distance` units when raycast hits nothing.
function getCameraForwardPoint(viewer: ViewerLike | null, distance = 8): WorldPos | null {
  const camera = viewer?.camera as (THREE.Camera & { position: THREE.Vector3 }) | undefined;
  if (!camera) return null;
  const dir = new THREE.Vector3();
  camera.getWorldDirection(dir);
  return {
    x: camera.position.x + dir.x * distance,
    y: camera.position.y + dir.y * distance,
    z: camera.position.z + dir.z * distance,
  };
}

const ENU_TO_THREEJS = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);

const FlythroughTool: React.FC<FlythroughToolProps> = ({
  viewerRef,
  isActive,
  onClose,
  anchorRef,
  initialGeoTarget,
  initialWorldTarget,
  autoStartFromCamera = false,
  initialOrbit,
  headless = false,
  interactiveRetarget = false,
  paused = false,
  restartOnResume = false,
  onManualNavigation,
  retargetArea,
}) => {
  const [step, setStep] = useState<FlythroughStep>('idle');
  const [orbitCenter, setOrbitCenter] = useState<OrbitCenter | null>(null);
  const [centerScreenPos, setCenterScreenPos] = useState<ScreenPos | null>(null);
  const [panelLeft, setPanelLeft] = useState<number | null>(null);
  const [previewRadius, setPreviewRadius] = useState<number>(MIN_ORBIT_RADIUS);
  const [orbitDurationMs, setOrbitDurationMs] = useState<number>(DEFAULT_ORBIT_DURATION_MS);
  const [previewScreenPoints, setPreviewScreenPoints] = useState<ScreenPos[]>([]);
  const [previewRadiusMax, setPreviewRadiusMax] = useState<number>(BASE_MAX_ORBIT_RADIUS);
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [recordedFlythrough, setRecordedFlythrough] = useState<RecordedFlythrough | null>(null);

  const orbitRafRef = useRef<number>(0);
  const overlayRafRef = useRef<number>(0);
  const frozenViewerStateRef = useRef<FrozenViewerState | null>(null);
  const autoStartedFromCameraRef = useRef(false);
  const autoStartedFromWorldTargetRef = useRef(false);
  const autoStartedOrbitRef = useRef(false);
  const orbitPausedRef = useRef(paused);
  const orbitCenterRef = useRef<OrbitCenter | null>(null);
  const stepRef = useRef<FlythroughStep>('idle');
  const pointerDownRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const interactivePointerRef = useRef<number | null>(null);
  const lastOrbitLogRef = useRef<number>(-1);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<BlobPart[]>([]);
  const isRecordingReplay = recordingState === 'recording' || recordingState === 'processing';

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    orbitCenterRef.current = orbitCenter;
  }, [orbitCenter]);

  useEffect(() => {
    if (!anchorRef?.current) return;

    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPanelLeft(window.innerWidth - rect.right);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef, isActive]);

  // Pre-set orbit center from agent-provided geo target (skips manual click step)
  useEffect(() => {
    if (!initialGeoTarget) return;
    const viewer = viewerRef.current;
    const obs = viewer?.observerCoordinates;
    if (!obs || obs.length < 3) return;

    const [obsLat, obsLon, obsAlt] = obs as [number, number, number];
    const enu = geodeticToENU(
      initialGeoTarget.latitude, initialGeoTarget.longitude, obsAlt,
      obsLat, obsLon, obsAlt,
    );

    const worldVec = new THREE.Vector3(enu.east, enu.north, enu.up)
      .applyQuaternion(ENU_TO_THREEJS);
    const worldPos: WorldPos = { x: worldVec.x, y: worldVec.y, z: worldVec.z };

    const camera = viewer?.camera;
    if (camera) {
      const defaultRadius = computeDefaultOrbitRadius(worldPos, camera as THREE.Camera & { position: THREE.Vector3 });
      setPreviewRadius(defaultRadius);
      setPreviewRadiusMax(computeRadiusSliderMax(defaultRadius));
    }

    setOrbitCenter({ worldPos });
    setStep('preview');
  }, [initialGeoTarget, viewerRef]);

  const stopOrbitAnimation = useCallback(() => {
    if (orbitRafRef.current) {
      cancelAnimationFrame(orbitRafRef.current);
      orbitRafRef.current = 0;
    }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    const stream = recordingStreamRef.current;

    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  }, []);

  const startRecording = useCallback((viewer: ViewerLike) => {
    const canvas = viewer.renderer?.domElement;

    if (!canvas?.captureStream) {
      setRecordingState('failed');
      setRecordingError('This browser does not support canvas recording.');
      return false;
    }

    if (typeof MediaRecorder === 'undefined') {
      setRecordingState('failed');
      setRecordingError('This browser does not support video recording.');
      return false;
    }

    const stream = canvas.captureStream(30);
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    const mimeType = mimeTypes.find((type) => MediaRecorder.isTypeSupported(type));

    try {
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      mediaRecorderRef.current = recorder;
      recordingStreamRef.current = stream;
      recordingChunksRef.current = [];
      setRecordingState('recording');
      setRecordingError(null);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        setRecordingState('failed');
        setRecordingError('Recording failed while capturing the flythrough.');
        stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
        recordingStreamRef.current = null;
      };

      recorder.onstop = () => {
        const chunks = recordingChunksRef.current.slice();
        recordingChunksRef.current = [];
        stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
        recordingStreamRef.current = null;

        if (!chunks.length) {
          setRecordingState('failed');
          setRecordingError('The flythrough ended before a recording was saved.');
          return;
        }

        setRecordingState('processing');
        const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        const filename = `flythrough-${Date.now()}.webm`;

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setRecordedFlythrough((current) => {
          if (current?.url) URL.revokeObjectURL(current.url);
          return { url, filename };
        });
        setRecordingState('ready');
      };

      recorder.start(250);
      return true;
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      recordingStreamRef.current = null;
      setRecordingState('failed');
      setRecordingError(error instanceof Error ? error.message : 'Recording could not be started.');
      return false;
    }
  }, []);

  const resetTool = useCallback(() => {
    stopOrbitAnimation();
    stopRecording();
    if (recordedFlythrough?.url) URL.revokeObjectURL(recordedFlythrough.url);
    setStep('idle');
    setOrbitCenter(null);
    setCenterScreenPos(null);
    setPreviewRadius(MIN_ORBIT_RADIUS);
    setPreviewRadiusMax(BASE_MAX_ORBIT_RADIUS);
    setOrbitDurationMs(DEFAULT_ORBIT_DURATION_MS);
    setPreviewScreenPoints([]);
    setRecordingState('idle');
    setRecordingError(null);
    setRecordedFlythrough(null);
    pointerDownRef.current = null;
    lastOrbitLogRef.current = -1;
  }, [recordedFlythrough?.url, stopOrbitAnimation, stopRecording]);

  useEffect(() => {
    const viewer = viewerRef.current;

    if (!isActive) {
      stopOrbitAnimation();
      restoreViewerControls(viewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
      autoStartedFromCameraRef.current = false;
      autoStartedFromWorldTargetRef.current = false;
      resetTool();
      return;
    }

    if (!frozenViewerStateRef.current && viewer && !orbitPausedRef.current) {
      frozenViewerStateRef.current = freezeViewerControls(viewer);
    }

    if (!autoStartFromCamera && !initialWorldTarget) setStep('capturing');

    return () => {
      stopOrbitAnimation();
      restoreViewerControls(viewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
    };
  }, [isActive, autoStartFromCamera, initialWorldTarget, resetTool, stopOrbitAnimation, viewerRef]);

  useEffect(() => {
    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera;
      const center = orbitCenterRef.current;

      if (canvas && camera && center) {
        setCenterScreenPos(projectToScreen(center.worldPos, camera, canvas));
        if (stepRef.current === 'preview') {
          const previewPoints = buildOrbitPreviewPoints(
            center.worldPos,
            previewRadius,
            ORBIT_PREVIEW_SAMPLES
          );
          setPreviewScreenPoints(previewPoints.map((point) => projectToScreen(point, camera, canvas)));
        } else {
          setPreviewScreenPoints([]);
        }
      } else {
        setCenterScreenPos(null);
        setPreviewScreenPoints([]);
      }

      overlayRafRef.current = requestAnimationFrame(tick);
    };

    overlayRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (overlayRafRef.current) {
        cancelAnimationFrame(overlayRafRef.current);
      }
    };
  }, [previewRadius, viewerRef]);

  const startOrbit = useCallback((center: OrbitCenter, requestedRadius: number, requestedDurationMs: number, preserveCurrentPosition = false) => {
    const viewer = viewerRef.current;
    const camera = viewer?.camera;

    if (!viewer || !camera || !camera.position) {
      console.warn('[FlythroughTool] Viewer camera unavailable for orbit');
      onClose();
      return;
    }

    stopOrbitAnimation();

    const startPosition = new THREE.Vector3(
      camera.position.x,
      camera.position.y,
      camera.position.z
    );

    const centerVector = new THREE.Vector3(
      center.worldPos.x,
      center.worldPos.y,
      center.worldPos.z
    );

    const radialOffset = new THREE.Vector3(
      startPosition.x - centerVector.x,
      0,
      startPosition.z - centerVector.z
    );
    let startAngle = Math.atan2(radialOffset.z, radialOffset.x);

    const heightOffset = startPosition.y - centerVector.y;

    let radius = preserveCurrentPosition ? worldDistanceXZ(startPosition, centerVector) : clampOrbitRadius(requestedRadius);
    const durationMs = clampOrbitDuration(requestedDurationMs);

    // For aerial cameras nearly directly above the center, XZ distance is near zero.
    // Fall back to a radius derived from the height offset so the orbit is visible.
    const aerialFallback = Math.abs(heightOffset) * 0.6;
    if (!preserveCurrentPosition && radius < 0.01) {
      const fallbackDirection = new THREE.Vector3(1, 0, 0);
      radius = Math.max(MIN_ORBIT_RADIUS, aerialFallback);
      radialOffset.copy(fallbackDirection.multiplyScalar(radius));
    } else if (!preserveCurrentPosition) {
      radius = Math.max(radius, MIN_ORBIT_RADIUS);
      if (radius < aerialFallback * 0.3 && radialOffset.length() > 0.001) {
        radius = aerialFallback;
        radialOffset.setLength(radius);
      }
    }

    const orbitDirection = radialOffset.clone().normalize();

    console.log('[FlythroughTool] Starting orbit', {
      center: center.worldPos,
      startPosition: {
        x: Number(startPosition.x.toFixed(3)),
        y: Number(startPosition.y.toFixed(3)),
        z: Number(startPosition.z.toFixed(3)),
      },
      radius: Number(radius.toFixed(3)),
      heightOffset: Number(heightOffset.toFixed(3)),
      durationMs,
    });
    console.log('[FlythroughTool] Orbit start', {
      center: center.worldPos,
      radius: Number(radius.toFixed(3)),
      durationMs,
    });

    setStep('orbiting');

    let orbitStartTime = performance.now();

    const animate = (now: number) => {
      const activeViewer = viewerRef.current;
      const activeCamera = activeViewer?.camera;

      if (!isActive || !activeViewer || !activeCamera) {
        stopOrbitAnimation();
        return;
      }

      const elapsed = now - orbitStartTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const easedProgress = restartOnResume ? progress : 0.5 - 0.5 * Math.cos(progress * Math.PI);
      const angle = startAngle + easedProgress * Math.PI * 2;
      const progressBucket = Math.floor(progress * 10);

      const nextX = centerVector.x + Math.cos(angle) * radius;
      const nextZ = centerVector.z + Math.sin(angle) * radius;
      const nextY = centerVector.y + heightOffset;

      activeCamera.position.set(nextX, nextY, nextZ);
      activeCamera.lookAt(centerVector);
      activeCamera.updateMatrixWorld();
      // Sync SparkViewer's interpolation buffers so SparkControls doesn't fight the orbit
      const av = activeViewer as any;
      if (av.nextCamera) av.nextCamera.copy(activeCamera);
      if (av.previousCamera) av.previousCamera.copy(activeCamera);
      // Clear SparkControls aerial spring/damping velocity every frame — it rebuilds
      // from camera delta each tick and will fight the orbit if left non-zero.
      const pc = av.sparkControls?.pointerControls as any;
      if (pc) {
        pc.rotateVelocity?.set?.(0, 0, 0);
        pc.moveVelocity?.set?.(0, 0, 0);
      }

      if (progressBucket !== lastOrbitLogRef.current) {
        lastOrbitLogRef.current = progressBucket;
        console.log('[FlythroughTool] Orbit progress', {
          progress: Number(progress.toFixed(3)),
          angle: Number(angle.toFixed(3)),
          cameraPosition: {
            x: Number(nextX.toFixed(3)),
            y: Number(nextY.toFixed(3)),
            z: Number(nextZ.toFixed(3)),
          },
        });
      }

      if (progress < 1) {
        orbitRafRef.current = requestAnimationFrame(animate);
        return;
      }

      if (initialOrbit?.loop) {
        orbitStartTime = now;
        startAngle += Math.PI * 2;
        lastOrbitLogRef.current = -1;
        orbitRafRef.current = requestAnimationFrame(animate);
        return;
      }

      const endPosition = centerVector
        .clone()
        .add(orbitDirection.multiplyScalar(radius))
        .setY(centerVector.y + heightOffset);

      activeCamera.position.copy(endPosition);
      activeCamera.lookAt(centerVector);
      activeCamera.updateMatrixWorld();
      const avEnd = activeViewer as any;
      if (avEnd.nextCamera) avEnd.nextCamera.copy(activeCamera);
      if (avEnd.previousCamera) avEnd.previousCamera.copy(activeCamera);

      console.log('[FlythroughTool] Orbit complete', {
        center: center.worldPos,
      });

      stopRecording();
      stopOrbitAnimation();
      setStep('complete');
      restoreViewerControls(activeViewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
    };

    orbitRafRef.current = requestAnimationFrame(animate);
  }, [initialOrbit?.loop, isActive, onClose, restartOnResume, stopOrbitAnimation, stopRecording, viewerRef]);

  useEffect(() => {
    if (!isActive || !initialOrbit || autoStartedOrbitRef.current) return;
    if (!viewerRef.current?.camera) return;

    const center: OrbitCenter = {
      worldPos: {
        x: initialOrbit.center[0],
        y: initialOrbit.center[1],
        z: initialOrbit.center[2],
      },
    };
    const radius = clampOrbitRadius(initialOrbit.radius);
    const durationMs = clampOrbitDuration(initialOrbit.durationMs);
    autoStartedOrbitRef.current = true;
    setOrbitCenter(center);
    setPreviewRadius(radius);
    setPreviewRadiusMax(computeRadiusSliderMax(radius));
    setOrbitDurationMs(durationMs);
    setStep('preview');

    if (initialOrbit.autoStart) {
      startOrbit(center, radius, durationMs);
    }

    return () => {
      // React Strict Mode performs a setup → cleanup → setup cycle in
      // development. Allow the second setup to restart an orbit cancelled by
      // the first cleanup instead of leaving the tool active but motionless.
      autoStartedOrbitRef.current = false;
    };
  }, [initialOrbit, isActive, startOrbit, viewerRef]);

  useEffect(() => {
    const wasPaused = orbitPausedRef.current;
    orbitPausedRef.current = paused;
    if (!isActive || !initialOrbit || wasPaused === paused) return;
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (paused) {
      stopOrbitAnimation();
      restoreViewerControls(viewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
      return;
    }

    const camera = viewer.camera as (THREE.Camera & { position: THREE.Vector3 }) | undefined;
    if (!camera) return;

    if (restartOnResume) {
      // Keep the fixed house center, but continue from the live position,
      // height, and zoom instead of teleporting to the landing camera.
      const center: OrbitCenter = { worldPos: { x: initialOrbit.center[0], y: initialOrbit.center[1], z: initialOrbit.center[2] } };
      setOrbitCenter(center);
      frozenViewerStateRef.current = freezeViewerControls(viewer);
      startOrbit(center, initialOrbit.radius, initialOrbit.durationMs, true);
      return;
    }

    // Navigation while paused changes both the camera position and its viewing
    // direction. Build the next orbit from that live pose instead of returning
    // to the center used before the pause.
    const lookedAt = pickWorldPointFromCenter(viewer) ?? getCameraForwardPoint(viewer, initialOrbit.radius);
    if (!lookedAt) return;
    const proposedCenter: WorldPos = lookedAt;
    const safeCenter = retargetArea
      ? clampWorldXZToCircle(
          proposedCenter,
          { x: retargetArea.center[0], z: retargetArea.center[2] },
          retargetArea.radius,
        )
      : proposedCenter;
    const center: OrbitCenter = { worldPos: safeCenter };
    setOrbitCenter(center);
    frozenViewerStateRef.current = freezeViewerControls(viewer);
    const frame = requestAnimationFrame(() => {
      startOrbit(center, initialOrbit.radius, initialOrbit.durationMs);
    });
    return () => cancelAnimationFrame(frame);
  }, [initialOrbit, isActive, paused, restartOnResume, retargetArea, startOrbit, stopOrbitAnimation, viewerRef]);

  // Stop synchronously in capture phase, before controls process the same input.
  useEffect(() => {
    if (!isActive || !onManualNavigation) return;
    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !canvas) return;
    const stopForInput = () => {
      if (orbitPausedRef.current) return;
      stopOrbitAnimation();
      restoreViewerControls(viewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
      orbitPausedRef.current = true;
      onManualNavigation();
    };
    const onKey = (event: KeyboardEvent) => {
      const element = event.target as HTMLElement | null;
      if (element?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyH'].includes(event.code)) stopForInput();
    };
    canvas.addEventListener('pointerdown', stopForInput, true);
    canvas.addEventListener('wheel', stopForInput, { capture: true, passive: true });
    window.addEventListener('keydown', onKey, true);
    return () => {
      canvas.removeEventListener('pointerdown', stopForInput, true);
      canvas.removeEventListener('wheel', stopForInput, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [isActive, onManualNavigation, stopOrbitAnimation, viewerRef]);

  useEffect(() => {
    if (!isActive || !initialOrbit || !interactiveRetarget || paused) return;
    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !canvas) return;

    const resumeOrbit = (event: PointerEvent) => {
      if (interactivePointerRef.current !== event.pointerId) return;
      interactivePointerRef.current = null;

      const pickedWorldPos = pickWorldPointFromCenter(viewer) ?? getCameraForwardPoint(viewer, initialOrbit.radius);
      const pickedInsideArea = pickedWorldPos && (!retargetArea || worldDistanceXZ(pickedWorldPos, {
        x: retargetArea.center[0],
        y: retargetArea.center[1],
        z: retargetArea.center[2],
      }) <= retargetArea.radius);
      const center = pickedInsideArea
        ? { worldPos: pickedWorldPos }
        : orbitCenterRef.current;
      if (!center) return;

      frozenViewerStateRef.current = freezeViewerControls(viewer);
      setOrbitCenter(center);
      setStep('orbiting');
      // Let Sparky finish processing the release before scripted camera
      // ownership resumes on the following frame.
      requestAnimationFrame(() => {
        if (isActive) startOrbit(center, initialOrbit.radius, initialOrbit.durationMs);
      });
    };

    const pauseOrbit = (event: PointerEvent) => {
      if (event.button !== 0 || interactivePointerRef.current !== null) return;
      interactivePointerRef.current = event.pointerId;
      stopOrbitAnimation();
      restoreViewerControls(viewer, frozenViewerStateRef.current);
      frozenViewerStateRef.current = null;
    };

    canvas.addEventListener('pointerdown', pauseOrbit);
    window.addEventListener('pointerup', resumeOrbit);
    window.addEventListener('pointercancel', resumeOrbit);
    return () => {
      interactivePointerRef.current = null;
      canvas.removeEventListener('pointerdown', pauseOrbit);
      window.removeEventListener('pointerup', resumeOrbit);
      window.removeEventListener('pointercancel', resumeOrbit);
    };
  }, [initialOrbit, interactiveRetarget, isActive, paused, retargetArea, startOrbit, stopOrbitAnimation, viewerRef]);

  // Preselect an explicit Three.js world-space target provided by the agent.
  // Stop at preview so the user can adjust the circle before starting the spin.
  useEffect(() => {
    if (!isActive || !initialWorldTarget || autoStartedFromWorldTargetRef.current) return;
    const viewer = viewerRef.current;
    const camera = viewer?.camera as (THREE.Camera & { position: THREE.Vector3 }) | undefined;
    if (!camera) return;

    const worldPos: WorldPos = {
      x: initialWorldTarget[0],
      y: initialWorldTarget[1],
      z: initialWorldTarget[2],
    };

    autoStartedFromWorldTargetRef.current = true;

    const center: OrbitCenter = { worldPos };
    const defaultRadius = clampOrbitRadius(computeDefaultOrbitRadius(worldPos, camera) / 2);
    setOrbitCenter(center);
    setPreviewRadius(defaultRadius);
    setPreviewRadiusMax(computeRadiusSliderMax(defaultRadius));
    setOrbitDurationMs(DEFAULT_ORBIT_DURATION_MS);
    setStep('preview');

    return () => {
      autoStartedFromWorldTargetRef.current = false;
    };
  }, [isActive, initialWorldTarget, viewerRef]);

  // When knowledge has no explicit target, preselect the camera look-at point and
  // still stop at preview. The user can correct the center or radius before spinning.
  useEffect(() => {
    if (!isActive || initialWorldTarget || !autoStartFromCamera || autoStartedFromCameraRef.current) return;
    const viewer = viewerRef.current;
    const camera = viewer?.camera as (THREE.Camera & { position: THREE.Vector3 }) | undefined;
    if (!camera) return;

    const worldPos =
      pickWorldPointFromCenter(viewer) ??
      getCameraForwardPoint(viewer, 8);

    if (!worldPos) return;

    autoStartedFromCameraRef.current = true;

    const center: OrbitCenter = { worldPos };
    const defaultRadius = clampOrbitRadius(computeDefaultOrbitRadius(worldPos, camera) / 2);
    setOrbitCenter(center);
    setPreviewRadius(defaultRadius);
    setPreviewRadiusMax(computeRadiusSliderMax(defaultRadius));
    setOrbitDurationMs(DEFAULT_ORBIT_DURATION_MS);
    setStep('preview');

    return () => {
      autoStartedFromCameraRef.current = false;
    };
  }, [isActive, autoStartFromCamera, initialWorldTarget, viewerRef]);

  const instructionText = useMemo(() => {
    if (step === 'capturing') return 'Choose one point in the scene to start the orbit.';
    if (step === 'preview') return 'Adjust the orbit, then record the 360° spin.';
    if (step === 'orbiting') return 'Spin in progress...';
    if (step === 'complete') return 'Spin complete. Record it or choose another point.';
    return 'Preparing spin...';
  }, [step]);

  const handlePickAgain = useCallback(() => {
    setOrbitCenter(null);
    setCenterScreenPos(null);
    setPreviewScreenPoints([]);
    setPreviewRadius(MIN_ORBIT_RADIUS);
    setPreviewRadiusMax(BASE_MAX_ORBIT_RADIUS);
    setOrbitDurationMs(DEFAULT_ORBIT_DURATION_MS);
    setStep('capturing');
  }, []);

  const handleRadiusChange = useCallback((radius: number) => {
    const nextRadius = clampOrbitRadius(radius);
    setPreviewRadius(nextRadius);
    console.log('[FlythroughTool] Radius updated', {
      radius: Number(nextRadius.toFixed(3)),
    });
  }, []);

  const handleStartFlythrough = useCallback(() => {
    const viewer = viewerRef.current;
    if (!orbitCenter || !viewer) return;
    startOrbit(orbitCenter, previewRadius, orbitDurationMs);
  }, [orbitCenter, orbitDurationMs, previewRadius, startOrbit, viewerRef]);

  const handleDownloadRecording = useCallback(() => {
    if (!recordedFlythrough?.url) return;
    const link = document.createElement('a');
    link.href = recordedFlythrough.url;
    link.download = recordedFlythrough.filename;
    link.rel = 'noopener';
    link.click();
  }, [recordedFlythrough]);

  const handleRecordReplay = useCallback(() => {
    const viewer = viewerRef.current;
    if (!orbitCenter || !viewer) return;

    if (recordedFlythrough?.url) {
      URL.revokeObjectURL(recordedFlythrough.url);
      setRecordedFlythrough(null);
    }

    setRecordingState('idle');
    setRecordingError(null);

    if (!startRecording(viewer)) return;
    startOrbit(orbitCenter, previewRadius, orbitDurationMs);
  }, [orbitCenter, orbitDurationMs, previewRadius, recordedFlythrough?.url, startOrbit, startRecording, viewerRef]);

  const previewSegments = useMemo(
    () => buildVisiblePolylineSegments(previewScreenPoints),
    [previewScreenPoints]
  );

  const blockViewerNavigation = useCallback((viewer: ViewerLike | null) => {
    if (!viewer) return;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera && viewer.camera) viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera && viewer.camera) viewer.previousCamera.copy(viewer.camera);
  }, []);

  const consumePointerEvent = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
  }, []);

  const consumeMouseEvent = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation?.();
  }, []);

  const handleOverlayPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    consumePointerEvent(event);
    const viewer = viewerRef.current;
    blockViewerNavigation(viewer);

    if (stepRef.current !== 'capturing') {
      pointerDownRef.current = null;
      return;
    }

    pointerDownRef.current = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
  }, [blockViewerNavigation, consumePointerEvent, viewerRef]);

  const handleOverlayPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    consumePointerEvent(event);
    const viewer = viewerRef.current;
    blockViewerNavigation(viewer);

    const pointerDown = pointerDownRef.current;
    pointerDownRef.current = null;

    if (!viewer || !pointerDown || stepRef.current !== 'capturing') {
      return;
    }

    const moved =
      Math.abs(event.clientX - pointerDown.x) > 6 ||
      Math.abs(event.clientY - pointerDown.y) > 6 ||
      event.pointerId !== pointerDown.pointerId;

    if (moved) {
      return;
    }

    const worldPos = pickWorldPointFromPointer(viewer, event.clientX, event.clientY);

    if (!worldPos) {
      console.warn('[FlythroughTool] Ray pick failed', {
        clientX: event.clientX,
        clientY: event.clientY,
      });
      return;
    }

    const pickedPoint: OrbitCenter = { worldPos };
    const defaultRadius = viewer.camera
      ? computeDefaultOrbitRadius(worldPos, viewer.camera)
      : MIN_ORBIT_RADIUS;

    console.log('[FlythroughTool] Picked world point', {
      x: Number(worldPos.x.toFixed(4)),
      y: Number(worldPos.y.toFixed(4)),
      z: Number(worldPos.z.toFixed(4)),
    });
    console.log('[FlythroughTool] Selected center', pickedPoint.worldPos);
    console.log('[FlythroughTool] Default radius chosen after pick', {
      radius: Number(defaultRadius.toFixed(3)),
    });

    setOrbitCenter(pickedPoint);
    setPreviewRadius(defaultRadius);
    setPreviewRadiusMax(computeRadiusSliderMax(defaultRadius));
    setOrbitDurationMs(DEFAULT_ORBIT_DURATION_MS);
    setStep('preview');
  }, [blockViewerNavigation, consumePointerEvent, viewerRef]);

  if (!isActive) return null;
  if (headless) return null;

  return (
    <>
      {(step === 'capturing' || step === 'orbiting') && (
        <div
          className="fixed inset-0 z-[1001]"
          style={{
            cursor: step === 'capturing' ? 'crosshair' : 'default',
            pointerEvents: 'auto',
            background: 'transparent',
          }}
          onPointerDown={handleOverlayPointerDown}
          onPointerUp={handleOverlayPointerUp}
          onClick={consumeMouseEvent}
        />
      )}

      {step === 'capturing' && (
        <style>{`canvas { cursor: crosshair !important; }`}</style>
      )}

      {step === 'preview' && previewSegments.length > 0 && (
        <svg
          className="fixed inset-0 z-[1002] pointer-events-none"
          style={{ width: '100vw', height: '100vh' }}
        >
          {previewSegments.map((segment, index) => (
            <g key={index}>
              <polyline
                points={segment}
                fill="none"
                stroke="rgba(253,186,89,0.2)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points={segment}
                fill="none"
                stroke="rgba(253,186,89,0.95)"
                strokeWidth="2"
                strokeDasharray="6 4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          ))}
        </svg>
      )}

      {centerScreenPos?.visible && !isRecordingReplay && (
        <div
          className="fixed z-[1003] pointer-events-none"
          style={{
            left: centerScreenPos.x,
            top: centerScreenPos.y,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid rgba(253,186,89,0.95)',
              boxShadow: '0 0 0 4px rgba(253,186,89,0.16)',
              background: 'rgba(255,255,255,0.08)',
            }}
          />
        </div>
      )}

      <div
        className="fixed z-[1002] w-[300px] rounded-2xl px-4 py-4 text-white shadow-xl"
        style={{
          top: 76,
          right: panelLeft ?? 16,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(10px) saturate(140%)',
          WebkitBackdropFilter: 'blur(10px) saturate(140%)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold tracking-wide">Spin</div>
            <div className="mt-1 text-xs text-white/70">
              {instructionText}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
            aria-label="Close spin tool"
          >
            ✕
          </button>
        </div>

        {orbitCenter && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/75">
            <div>
              Center: {orbitCenter.worldPos.x.toFixed(2)}, {orbitCenter.worldPos.y.toFixed(2)}, {orbitCenter.worldPos.z.toFixed(2)}
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-3">
              <div className="flex items-center justify-between text-xs text-white/70">
                <span>Radius</span>
                <span>{previewRadius.toFixed(1)} m</span>
              </div>
              <input
                type="range"
                min={MIN_ORBIT_RADIUS}
                max={previewRadiusMax}
                step={0.1}
                value={previewRadius}
                onChange={(event) => handleRadiusChange(Number(event.target.value))}
                className="mt-3 w-full accent-[#0CB4E4]"
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-3">
              <div className="flex items-center justify-between text-xs text-white/70">
                <span>Speed</span>
                <span>{getSpeedLabel(orbitDurationMs)}</span>
              </div>
              <input
                type="range"
                min={MIN_ORBIT_DURATION_MS}
                max={MAX_ORBIT_DURATION_MS}
                step={500}
                value={orbitDurationMs}
                onChange={(event) => setOrbitDurationMs(clampOrbitDuration(Number(event.target.value)))}
                className="mt-3 w-full accent-[#0CB4E4]"
              />
              <div className="mt-2 text-[11px] text-white/55">
                360 in {(orbitDurationMs / 1000).toFixed(0)}s
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleStartFlythrough}
                className="rounded-xl bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#4FCBEE]"
              >
                Start Spin
              </button>
              <button
                onClick={onClose}
                className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="mt-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleRecordReplay}
                className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              >
                Record this flythrough
              </button>
              <button
                onClick={handlePickAgain}
                className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              >
                Choose another point
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onClose}
                className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors hover:bg-white/10"
              >
                Close
              </button>
            </div>
            {recordingState === 'ready' && recordedFlythrough?.url && (
              <button
                onClick={handleDownloadRecording}
                className="w-full rounded-xl bg-[#0CB4E4] px-3 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#4FCBEE]"
              >
                Download Recording
              </button>
            )}
            {recordingState === 'processing' && (
              <div className="text-center text-xs text-white/55">
                Preparing recording and starting download...
              </div>
            )}
            {recordingState === 'failed' && recordingError && (
              <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-100">
                {recordingError}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FlythroughTool;
